import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, RotateCcw, ArrowRight, Loader2, Plus } from "lucide-react";
import { api, eur } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const OCCASIONS = [
  { key: "pizza", label: "Pizza o Fast Food Gourmet", hint: "Bollicine e rossi leggeri" },
  { key: "sushi", label: "Sushi e Crudi di mare", hint: "Bianchi minerali e rosati" },
  { key: "grigliata", label: "Grigliata o Piatti strutturati", hint: "Grandi rossi" },
  { key: "aperitivo", label: "Aperitivo Veg e stuzzichini", hint: "Metodo Classico e bianchi" },
];

const TASTES = [
  { key: "intenso", label: "Amo i sapori intensi e decisi" },
  { key: "fresco", label: "Preferisco la freschezza e la leggerezza" },
];

const imgSrc = (url) => (url && url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

export default function SommelierQuiz() {
  const { add } = useCart();
  const [step, setStep] = useState(0); // 0 occasione, 1 gusto, 2 risultato
  const [occasion, setOccasion] = useState("");
  const [taste, setTaste] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  const reset = () => { setStep(0); setOccasion(""); setTaste(""); setResults([]); };

  const pickOccasion = (k) => { setOccasion(k); setStep(1); };

  const pickTaste = async (k) => {
    setTaste(k);
    setLoading(true);
    setStep(2);
    try {
      const { data } = await api.post("/sommelier/suggest", { occasion, taste: k });
      setResults(data.wines || []);
    } catch {
      toast.error("Impossibile ottenere i consigli. Riprova.");
      setResults([]);
    } finally { setLoading(false); }
  };

  const progress = step === 2 ? 100 : (step / 2) * 100;

  return (
    <section className="bg-ink text-bone py-24 lg:py-32" data-testid="sommelier-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles size={18} className="text-claret-soft" />
          <p className="text-xs uppercase tracking-[0.3em] text-claret-soft">Sommelier Virtuale</p>
        </div>
        <h2 className="font-serif-display text-[9vw] lg:text-7xl leading-[0.95] mb-4">Trova il tuo vino.</h2>
        <p className="text-bone/60 max-w-xl mb-12">Due domande e tre bottiglie su misura per te.</p>

        {/* progress */}
        <div className="h-px bg-bone/15 mb-12 relative overflow-hidden">
          <motion.div className="absolute inset-y-0 left-0 bg-claret" animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} />
        </div>

        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="q1" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -24 }} transition={{ duration: 0.5 }}>
              <p className="font-serif-display text-3xl lg:text-4xl mb-8">Cosa c'è nel piatto?</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {OCCASIONS.map((o) => (
                  <button
                    key={o.key}
                    data-testid={`sommelier-occasion-${o.key}`}
                    onClick={() => pickOccasion(o.key)}
                    className="group text-left border border-bone/20 hover:border-claret hover:bg-claret/10 p-6 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-serif-display text-2xl">{o.label}</span>
                      <ArrowRight size={18} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all text-claret-soft" />
                    </div>
                    <span className="text-xs uppercase tracking-wide text-bone/70 mt-2 block">{o.hint}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div key="q2" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -24 }} transition={{ duration: 0.5 }}>
              <p className="font-serif-display text-3xl lg:text-4xl mb-8">Che tipo sei?</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {TASTES.map((t) => (
                  <button
                    key={t.key}
                    data-testid={`sommelier-taste-${t.key}`}
                    onClick={() => pickTaste(t.key)}
                    className="group text-left border border-bone/20 hover:border-claret hover:bg-claret/10 p-8 transition-colors"
                  >
                    <span className="font-serif-display text-2xl lg:text-3xl">{t.label}</span>
                  </button>
                ))}
              </div>
              <button onClick={() => setStep(0)} data-testid="sommelier-back" className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-wide text-bone/50 hover:text-bone transition-colors">
                <RotateCcw size={14} /> Domanda precedente
              </button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="res" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} data-testid="sommelier-results">
              {loading ? (
                <div className="flex items-center gap-3 text-bone/60 py-16"><Loader2 className="animate-spin" size={20} /> Il sommelier sta scegliendo per te…</div>
              ) : (
                <>
                  <p className="font-serif-display text-3xl lg:text-4xl mb-10">I nostri consigli per te.</p>
                  <div className="grid md:grid-cols-3 gap-8">
                    {results.map((w, i) => (
                      <motion.div
                        key={w.id}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: i * 0.12 }}
                        data-testid={`sommelier-wine-${w.id}`}
                        className="bg-bone text-ink flex flex-col"
                      >
                        <Link to={`/wine/${w.id}`} className="block img-zoom aspect-[3/4] overflow-hidden bg-linen">
                          <img src={imgSrc(w.image)} alt={w.name} loading="lazy" className="w-full h-full object-cover" />
                        </Link>
                        <div className="p-5 flex flex-col flex-1">
                          <Link to={`/wine/${w.id}`}>
                            <h3 className="font-serif-display text-2xl leading-tight hover:text-claret transition-colors">{w.name}</h3>
                          </Link>
                          <p className="text-xs uppercase tracking-wide text-ink-soft mt-1">{w.producer}{w.region ? ` · ${w.region}` : ""}</p>
                          <div className="mt-3 border-l-2 border-claret pl-3">
                            <p className="text-sm text-ink-soft italic leading-relaxed">{w.match_reason}</p>
                          </div>
                          <div className="mt-auto pt-5 flex items-center justify-between">
                            <span className="text-lg">{eur(w.price)}</span>
                            <button
                              data-testid={`sommelier-add-${w.id}`}
                              onClick={() => { add(w); toast.success(`${w.name} aggiunto`); }}
                              className="inline-flex items-center gap-2 bg-ink text-bone px-4 py-2.5 text-xs uppercase tracking-wide hover:bg-claret transition-colors"
                            >
                              <Plus size={14} /> Aggiungi
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  <button onClick={reset} data-testid="sommelier-restart" className="mt-12 inline-flex items-center gap-2 border border-bone/40 hover:bg-bone hover:text-ink px-6 py-3 uppercase tracking-[0.15em] text-sm transition-colors">
                    <RotateCcw size={16} /> Rifai il test
                  </button>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
